import { useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import ParchmentCard from '@/components/ui/ParchmentCard';
import * as friendService from '@/services/friendService';
import { resolveAvatarUrl } from '@/utils/avatar';
import useNotificationStore from '@/store/notificationStore';

const avatarFallback = '/avatars/avatar_1.png';

const displayName = (player) => player?.displayName || player?.username || 'Jogador';

const statItems = (player) => [
  ['Nível', player?.level || 1],
  ['POIs', player?.totalPoisVisited || 0],
  ['Missões', player?.totalMissionsCompleted || 0],
  ['Ofertas', player?.totalRedemptions || 0],
];

function PlayerAvatar({ player, size = 'h-12 w-12' }) {
  return (
    <div className={`${size} flex-shrink-0 overflow-hidden rounded-full border-2 border-accent/60 bg-surfaceAlt`}>
      <img src={resolveAvatarUrl(player?.avatarUrl || avatarFallback)} alt="" className="h-full w-full object-cover" />
    </div>
  );
}

function MiniStats({ player }) {
  return (
    <div className="mt-2 grid grid-cols-4 gap-1 text-center">
      {statItems(player).map(([label, value]) => (
        <div key={label} className="rounded-lg bg-surfaceAlt/8 px-1 py-1.5">
          <strong className="block font-serif text-sm leading-none text-fg">{value}</strong>
          <span className="text-[9px] font-black uppercase text-fg/55">{label}</span>
        </div>
      ))}
    </div>
  );
}

function FriendCard({ item }) {
  const player = item.player || item;
  return (
    <ParchmentCard className="text-fg">
      <div className="flex items-center gap-3">
        <PlayerAvatar player={player} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-serif text-base font-black">{displayName(player)}</h2>
          <p className="truncate text-xs font-bold text-fg/55">@{player.username || 'jogador'} · nível {player.level || 1}</p>
        </div>
      </div>
      <MiniStats player={player} />
    </ParchmentCard>
  );
}

function RequestCard({ item, onAccept, onReject, busyId }) {
  const player = item.player;
  return (
    <ParchmentCard className="text-fg">
      <div className="flex items-start gap-3">
        <PlayerAvatar player={player} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-serif text-base font-black">{displayName(player)}</h2>
          <p className="text-xs font-bold text-fg/60">@{player.username} quer juntar-se à tua tripulação.</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button variant="green" className="rounded-lg px-3 py-2 text-xs" disabled={busyId === item.id} onClick={() => onAccept(item.id)}>
          Aceitar
        </Button>
        <Button variant="ghost" className="rounded-lg px-3 py-2 text-xs text-pirate-red" disabled={busyId === item.id} onClick={() => onReject(item.id)}>
          Recusar
        </Button>
      </div>
    </ParchmentCard>
  );
}

function SearchResult({ player, onSend, onAccept, busyId }) {
  const relation = player.friendship;
  const isAccepted = relation?.status === 'accepted';
  const sent = relation?.status === 'pending' && relation?.direction === 'sent';
  const received = relation?.status === 'pending' && relation?.direction === 'received';

  return (
    <ParchmentCard className="text-fg">
      <div className="flex items-center gap-3">
        <PlayerAvatar player={player} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-serif text-base font-black">{displayName(player)}</h2>
          <p className="truncate text-xs font-bold text-fg/55">@{player.username} · nível {player.level || 1}</p>
        </div>
        {isAccepted ? (
          <span className="rounded-full bg-forest/15 px-2 py-1 text-[10px] font-black uppercase text-forest-light">Amigo</span>
        ) : sent ? (
          <span className="rounded-full bg-accent/20 px-2 py-1 text-[10px] font-black uppercase text-accent">Enviado</span>
        ) : received ? (
          <Button variant="green" className="rounded-lg px-3 py-2 text-xs" disabled={busyId === relation.id} onClick={() => onAccept(relation.id)}>
            Aceitar
          </Button>
        ) : (
          <Button variant="gold" className="rounded-lg px-3 py-2 text-xs" disabled={busyId === player.id} onClick={() => onSend(player.id)}>
            Convidar
          </Button>
        )}
      </div>
    </ParchmentCard>
  );
}

export default function FriendsPage() {
  const [dashboard, setDashboard] = useState({ friends: [], incoming: [], outgoing: [] });
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [tab, setTab] = useState('friends');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const setFriendRequestCount = useNotificationStore((state) => state.setFriendRequestCount);

  const totalPending = dashboard.incoming.length + dashboard.outgoing.length;

  const tabs = useMemo(() => [
    { value: 'friends', label: `Amigos ${dashboard.friends.length}` },
    { value: 'requests', label: `Pedidos ${totalPending}` },
    { value: 'search', label: 'Procurar' },
  ], [dashboard.friends.length, totalPending]);

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await friendService.getDashboard();
      const nextDashboard = res.data.data || { friends: [], incoming: [], outgoing: [] };
      setDashboard(nextDashboard);
      setFriendRequestCount((nextDashboard.incoming || []).length);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar amigos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    const clean = query.trim();
    if (clean.length < 2) {
      setResults([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      setError('');
      try {
        const res = await friendService.searchPlayers(clean);
        setResults(res.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Não foi possível procurar jogadores.');
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const sendRequest = async (receiverId) => {
    setBusyId(receiverId);
    setNotice('');
    setError('');
    try {
      await friendService.sendRequest(receiverId);
      setNotice('Convite enviado.');
      await loadDashboard();
      const res = await friendService.searchPlayers(query.trim());
      setResults(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível enviar o convite.');
    } finally {
      setBusyId(null);
    }
  };

  const acceptRequest = async (friendshipId) => {
    setBusyId(friendshipId);
    setNotice('');
    setError('');
    try {
      await friendService.acceptRequest(friendshipId);
      setNotice('Pedido aceite. Novo amigo adicionado.');
      await loadDashboard();
      if (query.trim().length >= 2) {
        const res = await friendService.searchPlayers(query.trim());
        setResults(res.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível aceitar o pedido.');
    } finally {
      setBusyId(null);
    }
  };

  const rejectRequest = async (friendshipId) => {
    setBusyId(friendshipId);
    setNotice('');
    setError('');
    try {
      await friendService.rejectRequest(friendshipId);
      setNotice('Pedido recusado.');
      await loadDashboard();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível recusar o pedido.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main className="h-full overflow-y-auto bg-background px-4 pb-4 pt-4">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.18em] text-muted">Tripulação</p>
        <h1 className="font-urban text-2xl font-black text-fg">Amigos</h1>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {tabs.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setTab(item.value)}
            className={`rounded-full border px-2 py-2 text-[11px] font-black transition ${
              tab === item.value ? 'border-accent bg-accent text-white' : 'border-edge bg-surface text-muted'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {notice && <ParchmentCard className="mt-3 py-3 text-sm font-bold text-forest-light">{notice}</ParchmentCard>}
      {error && <ParchmentCard className="mt-3 py-3 text-sm font-bold text-pirate-red">{error}</ParchmentCard>}

      {tab === 'friends' && (
        <section className="mt-4 grid gap-3">
          {dashboard.friends.map((friendship) => <FriendCard key={friendship.id} item={friendship} />)}
          {loading && <ParchmentCard className="text-sm font-bold text-fg/70">A carregar amigos...</ParchmentCard>}
          {!loading && dashboard.friends.length === 0 && (
            <ParchmentCard className="text-sm font-bold text-fg/70">Ainda não tens amigos. Procura por username para convidar alguém.</ParchmentCard>
          )}
        </section>
      )}

      {tab === 'requests' && (
        <section className="mt-4 grid gap-3">
          {dashboard.incoming.map((item) => (
            <RequestCard key={item.id} item={item} onAccept={acceptRequest} onReject={rejectRequest} busyId={busyId} />
          ))}
          {dashboard.outgoing.map((item) => (
            <ParchmentCard key={item.id} className="text-fg">
              <div className="flex items-center gap-3">
                <PlayerAvatar player={item.player} />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-serif text-base font-black">{displayName(item.player)}</h2>
                  <p className="text-xs font-bold text-fg/60">Convite enviado, a aguardar resposta.</p>
                </div>
                <span className="rounded-full bg-accent/20 px-2 py-1 text-[10px] font-black uppercase text-accent">Pendente</span>
              </div>
            </ParchmentCard>
          ))}
          {!loading && totalPending === 0 && (
            <ParchmentCard className="text-sm font-bold text-fg/70">Não tens pedidos pendentes.</ParchmentCard>
          )}
        </section>
      )}

      {tab === 'search' && (
        <section className="mt-4">
          <label className="block">
            <span className="text-xs font-black uppercase tracking-[0.14em] text-muted">Username ou nome</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="mt-2 w-full rounded-xl border-2 border-edge bg-surface px-4 py-3 font-bold text-fg outline-none focus:border-accent"
              placeholder="Ex: leocoelho"
            />
          </label>

          <div className="mt-4 grid gap-3">
            {results.map((player) => (
              <SearchResult key={player.id} player={player} onSend={sendRequest} onAccept={acceptRequest} busyId={busyId} />
            ))}
            {searching && <ParchmentCard className="text-sm font-bold text-fg/70">A procurar jogadores...</ParchmentCard>}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <ParchmentCard className="text-sm font-bold text-fg/70">Nenhum jogador encontrado.</ParchmentCard>
            )}
          </div>
        </section>
      )}
    </main>
  );
}
